import axios, { AxiosRequestConfig } from 'axios';

const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8004/api/v1';

// Backend base URL (without /api/v1) for storage files
export const storageBaseUrl = apiBaseUrl.replace(/\/api\/v1\/?$/, '');

// Returns full URL for a storage file path (e.g. "employees/photos/abc.png" → "http://...host/storage/employees/photos/abc.png")
export const storageUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${storageBaseUrl}/storage/${path}`;
};

// Custom API client that returns data directly (response.data)
const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
});

if (/ngrok/i.test(apiBaseUrl)) {
  axiosInstance.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';
}

// Add token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle responses - extract data directly
axiosInstance.interceptors.response.use(
  (response) => {
    // Don't unwrap blob/arraybuffer responses — they're already binary data
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return response.data;
    }
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error.response?.data || error);
  }
);

// Create typed wrapper that returns data directly
const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance.get(url, config) as Promise<T>,
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance.post(url, data, config) as Promise<T>,
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance.put(url, data, config) as Promise<T>,
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance.patch(url, data, config) as Promise<T>,
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance.delete(url, config) as Promise<T>,
};

// Export base URL for direct downloads
export const API_BASE_URL = apiBaseUrl;

export default api;
